import { useEffect, useState } from 'react';
import { ITipOption } from '../../../../modules/consumer/appointments/tipping';

export const INVALID_AMOUNT_ERROR = 'Please enter a valid custom tip amount.';

export type SubmitTipParameters = {
  alreadyTipped: boolean;
  amount: number;
  onError: (error: string) => void;
  percentage: number;
  tipOption?: ITipOption | 'custom';
};

export type SubmitTipFunction = (params: SubmitTipParameters) => void;

export const useTip = (submitTip: SubmitTipFunction) => {
  const [customTip, setCustomTip] = useState<string>('');
  const [customTipValue, setCustomTipValue] = useState<number>(0);
  const [tipOption, setTipOption] = useState<ITipOption | 'custom'>(null);
  const [tipAmount, setTipAmount] = useState<number>(0);
  const [tipError, onError] = useState<string>(null);

  /**
   * Submits an empty tip, since the client has already tipped via a different medium.
   */
  const onAlreadyTipped = () => {
    setTipOption(null);
    setTipAmount(0);
    setCustomTipValue(0);
    submitTip({
      alreadyTipped: true,
      amount: 0,
      onError,
      percentage: 0,
      tipOption,
    });
  };

  const onCustomTipFocus = () => {
    setTipOption('custom');
    onError(null);
  };

  /**
   * Updates the tip amount state values based on the input
   * @param value The most recent value of the tip field
   */
  const onCustomTipChange = (value: string) => {
    setCustomTip(value);
  };

  /**
   * Changes the tip option selection
   * @param selectedOption The selected tip option
   */
  const onSelectTip = (selectedOption?: ITipOption) => {
    setCustomTip('0');
    setTipOption(selectedOption);
    onError(null);
    setTipAmount(selectedOption?.amount || 0);
  };

  /**
   * Submits the selected tip, causing the client to be charged
   */
  const onSubmitTip = () => {
    if (tipOption === 'custom') {
      // Custom amount
      submitTip({
        alreadyTipped: false,
        amount: tipAmount,
        onError,
        percentage: null,
        tipOption,
      });
    } else if (tipOption) {
      // Selected percentage
      submitTip({
        alreadyTipped: false,
        amount: tipOption.amount,
        onError,
        percentage: tipOption.percentage,
        tipOption,
      });
    } else {
      onError(INVALID_AMOUNT_ERROR); // Unable to handle submit
    }
  };

  useEffect(() => {
    if (tipOption === 'custom') {
      const parsedValue = parseFloat((customTip || '').replace(/[^0-9\-.]/g, ''));

      if (Number.isNaN(parsedValue) || parsedValue < 0) {
        onError(INVALID_AMOUNT_ERROR);
        setTipAmount(0);
        setCustomTipValue(0);
      } else {
        onError(null);
        setTipAmount(parsedValue);
        setCustomTipValue(parsedValue);
      }
    }
  }, [customTip, onError, setCustomTipValue, setTipAmount, tipOption]);

  return {
    customTip,
    customTipValue,
    onAlreadyTipped,
    onCustomTipChange,
    onCustomTipFocus,
    onSelectTip,
    onSubmitTip,
    tipAmount,
    tipError,
    tipOption,
  };
};
